import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Badge,
  Box,
  Text,
} from '@chakra-ui/react';

import Navbar from '../../GlobalComponents/Navbar/Navbar';
import { Order } from '../Orders/Component/Order';
import { getPocoOrdersBySubscriberId } from '../Orders/Actions/actions';
import { getAllSubscriptionOfSubscriber, getSubscriberById } from '../../http/index';
import { normalizeSubscription } from '../Orders/Utils/utils';
import { PocoOrderType, SubscriptionData } from '../Orders/Types/types';
import styles from './Subscription.module.css';

// ---------------------------------------------------------------------------
// State interface
// ---------------------------------------------------------------------------
interface OrdersState {
  ordersLoading: boolean;
  ordersLoaded: boolean;
  orders: PocoOrderType[];
  ordersError: boolean;
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------
export const SubscriptionPage = () => {
  const navigate = useNavigate();

  // Redux reads (same selectors as Orders.tsx)
  const subscriberId = useSelector((state: any) => state.subscriber.subscriberId);
  const userId = useSelector((state: any) => state?.user?.userId || state?.subscriber?.userId);
  const typeOfSubscriber = useSelector((state: any) => state?.sbsSusbscriber?.typeOfSubscriber);

  // Derived flags
  const isB2CCustomer = typeOfSubscriber === 'Business to customer';

  // -------------------------------------------------------------------------
  // State
  // -------------------------------------------------------------------------
  const [ordersState, setOrdersState] = useState<OrdersState>({
    ordersLoading: false,
    ordersLoaded: false,
    orders: [],
    ordersError: false,
  });

  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | null>(null);
  const [isSubscriptionActive, setIsSubscriptionActive] = useState<boolean>(false);

  // -------------------------------------------------------------------------
  // Derived values
  // -------------------------------------------------------------------------
  const paidOrders = ordersState.orders
    .filter(o => o.paymentStatus === 'paid' && o.orderFor.toLowerCase() === 'subscription')
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

  const shouldShowSubscription = isB2CCustomer ? subscriptionData !== null : true;
  const normalized = subscriptionData ? normalizeSubscription(subscriptionData) : null;
  const planIsActive = isB2CCustomer ? (normalized?.isActive ?? false) : isSubscriptionActive;

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------
  const handleRenewSubscription = () => {
    navigate('/subscription?isRenewal=true');
  };

  const isRenewalAvailable = () => {
    if (!normalized?.expiryDate) return false;
    const oneMonthBeforeExpiry = new Date(normalized.expiryDate);
    oneMonthBeforeExpiry.setMonth(oneMonthBeforeExpiry.getMonth() - 1);
    return new Date() >= oneMonthBeforeExpiry;
  };

  // -------------------------------------------------------------------------
  // Data fetching — parallel fetches on mount
  // -------------------------------------------------------------------------
  useEffect(() => {
    // 1. Fetch poco orders (B2C only)
    const fetchOrders = async () => {
      if (!isB2CCustomer) return;
      setOrdersState({
        ordersLoading: true,
        ordersLoaded: false,
        orders: [],
        ordersError: false,
      });
      try {
        const res = await getPocoOrdersBySubscriberId(subscriberId);
        const sorted: PocoOrderType[] = (res.data ?? []).sort(
          (a: PocoOrderType, b: PocoOrderType) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        setOrdersState({
          ordersLoading: false,
          ordersLoaded: true,
          orders: sorted,
          ordersError: false,
        });
      } catch {
        setOrdersState({
          ordersLoading: false,
          ordersLoaded: false,
          orders: [],
          ordersError: true,
        });
      }
    };

    // 2. Fetch subscription details (B2C only — non-B2C has no Razorpay subscription)
    const fetchSubscriptionDetails = async () => {
      if (!userId || !isB2CCustomer) return;
      try {
        const res = await getAllSubscriptionOfSubscriber(userId);
        const subscriptions = res?.data?.data ?? res?.data;
        if (Array.isArray(subscriptions) && subscriptions.length > 0) {
          setSubscriptionData(subscriptions[0]);
        }
      } catch {
        setSubscriptionData(null);
      }
    };

    // 3. Fetch subscriber record to get isSubscriptionActive (for non-B2C)
    const fetchSubscriberStatus = async () => {
      if (!subscriberId || isB2CCustomer) return;
      try {
        const res = await getSubscriberById(subscriberId);
        const subscriber = res?.data?.data ?? res?.data;
        setIsSubscriptionActive(subscriber?.isSubscriptionActive ?? false);
      } catch {
        setIsSubscriptionActive(false);
      }
    };

    // Fire all in parallel
    fetchOrders();
    fetchSubscriptionDetails();
    fetchSubscriberStatus();
  }, [subscriberId, userId, isB2CCustomer]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <>
      <Navbar />
      <div className={styles.container}>
        {/* Subscription status card */}
        {shouldShowSubscription && (
          <div className={styles['subscription-wrapper']}>
            <Box className={styles['subscription-card']}>
              <div className={styles['subscription-header']}>
                <h3 className={styles['subscription-title']}>Subscription Status</h3>
                <span
                  className={`${styles['subscription-status']} ${
                    planIsActive ? '' : styles['status-inactive']
                  }`}
                >
                  {planIsActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              {isB2CCustomer && normalized && (
                <div className={styles['subscription-details']}>
                  <div className={styles['subscription-field']}>
                    <span className={styles['field-label']}>Start Date</span>
                    <span className={styles['field-value']}>
                      {normalized.startDate
                        ? normalized.startDate.toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: '2-digit',
                          })
                        : 'N/A'}
                    </span>
                  </div>
                  <div className={styles['subscription-field']}>
                    <span className={styles['field-label']}>Expiry Date</span>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span className={styles['field-value']}>
                        {normalized.expiryDate
                          ? normalized.expiryDate.toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: '2-digit',
                            })
                          : '-'}
                      </span>
                      {planIsActive && isRenewalAvailable() && (
                        <button
                          className={styles['renewal-button']}
                          onClick={handleRenewSubscription}
                        >
                          Renew Subscription
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Box>
          </div>
        )}

        {/* Subscription History section (B2C only) */}
        {isB2CCustomer && (
          <div className={styles['history-section']}>
            <div className={styles['history-header']}>
              <h3 className={styles['history-title']}>Subscription History</h3>
              {ordersState.ordersLoaded && !ordersState.ordersError && (
                <Badge colorScheme="teal" fontSize="12px" px={2} py={1} borderRadius="4px">
                  {paidOrders.length}
                </Badge>
              )}
            </div>

            <div className={styles['content-area']}>
              {ordersState.ordersLoading && (
                <Text className={styles['state-message']}>
                  Loading your orders…
                </Text>
              )}

              {ordersState.ordersError && (
                <Text className={styles['error-message']}>
                  Failed to load orders. Please try again.
                </Text>
              )}

              {ordersState.ordersLoaded && !ordersState.ordersError && (
                <>
                  {/* Paid orders list */}
                  {paidOrders.length > 0 ? (
                    paidOrders.map((order: PocoOrderType) => (
                      <Order key={order._id} pocoOrder={order} />
                    ))
                  ) : (
                    <Text className={styles['state-message']}>No orders found.</Text>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};
